import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import { PhotoSlider } from "react-photo-view"
import "react-photo-view/dist/react-photo-view.css"
import { ImageTopToolbar } from "./ImageTopToolbar"
import { ImageBottomToolbar } from "./ImageBottomToolbar"
import {
  DeliveryOrdersMap,
  DocumentDriverContext,
  Message,
  MessageDisplayType,
} from "../../reducers/messagesSlice"
import { mapFileUrlToSameOrigin } from "../message/utils/mapFileToSameOrigin"
import Loader from "../Loader"
import { useAppDispatch, useAppSelector } from "../../reducers/hooks"
import {
  hideImageGallery,
  selectImageGallery,
} from "../../reducers/overlaysSlice"
import { isMobile } from "../../utils/detectDevice"
import { mobileRoutes, setMobileRoute } from "../../routes/routes"
import { selectActiveChat } from "../../reducers/chatsSliceSelectors"
import { selectDeliveryOrdersFromChat } from "../../reducers/messagesSliceSelectors"
import ChatHelpers from "../../lib/chatHelpers"
import { StructuredMessage } from "components/slickAI/structuredMessage/types"
import { getSlickshiftAIJid } from "@/jidUtils"
import { XmppContext } from "@/stream/xmppClient"
import CMRMetaData from "@/components/cmr/cmr-meta-data"
import useCMRMetadata from "@/hooks/useCMRMetadata"
import { ArchivedChatContext } from "@/contexts/ArchivedChatContext"

export type DirectImageEntry = {
  src: string
  message?: Message
}

import type { ImageMetadataOverride } from "@/reducers/overlaysSlice"
export type { ImageMetadataOverride }

export type ImageLinkInfo = {
  cmrMessageId: string
  cmrFileUrl: string
  cmrFileName: string
  chatJid: string
  driverJid: string
  /** When present, the document is already linked and can be unlinked */
  unlinkInfo?: {
    deliveryOrderMessageId: string
    messageRoomJid: string
  }
}

export type ImageGalleryProps = {
  portalContainer?: HTMLElement
  messages: Message[]
  metadataOverride?: Record<string, string>
  driverContextOverride?: DocumentDriverContext
  documentTypeOverride?: string
  /** Multiple direct images for navigation (e.g. all CMR docs for a delivery order) */
  directImages?: DirectImageEntry[]
  /** Per-image metadata keyed by mapped src URL (used with directImages) */
  metadataOverrides?: Record<string, ImageMetadataOverride>
  /** Per-image link info keyed by mapped src URL */
  linkInfoMap?: Record<string, ImageLinkInfo>
  onLinked?: () => void
}

const extractMarkdownUrls = (markdown: string) => {
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g
  const urls = []
  let match

  while ((match = regex.exec(markdown)) !== null) {
    urls.push(match[2])
  }
  return urls
}

export function extractUrlsFromStructuredMessage(body: string): string[] {
  try {
    const message: StructuredMessage = JSON.parse(body)

    const urls: Set<string> = new Set()

    for (const block of message.blocks) {
      if (block.type === "image") {
        urls.add(block.url)
      } else if (block.type === "table") {
        for (const row of block.rows) {
          for (const cell of row) {
            if (typeof cell === "object" && "url" in cell) {
              urls.add(cell.url)
            }
          }
        }
      }
    }
    return Array.from(urls)
  } catch (e) {
    return []
  }
}

const isMessageWithImage = (message: Message) => {
  const m = message.forwarded || message
  return (
    !m.retracted &&
    m.file &&
    m.url &&
    (/\.(gif|png|jpe?g|webp|avif|svg|bmp)/i.test(m.url) || m.thumbnail?.uri)
  )
}

const isMarkdownWithImages = (message: Message) => {
  const markdownLinks = extractMarkdownUrls(message.body)
  return markdownLinks.length > 0
}

const isBlockWithImages = (message: Message) => {
  const urls = extractUrlsFromStructuredMessage(message.body)
  return urls.length > 0
}

const isMessageWithOrder = (
  message: Message,
  deliveryOrders?: DeliveryOrdersMap,
) => {
  return (
    Boolean(
      deliveryOrders &&
        deliveryOrders[message.id] &&
        !/\.pdf$/i.test(deliveryOrders[message.id].fileUrl ?? "") &&
        !deliveryOrders[message.id].files?.every((file) =>
          /\.pdf$/i.test(file.fileUrl),
        ),
    ) ||
    message.id === "order" ||
    message.finishedRouteAlert
  )
}

export default function ImageGallery({
  messages,
  metadataOverride,
  driverContextOverride,
  documentTypeOverride,
  directImages,
  metadataOverrides,
  linkInfoMap,
  onLinked,
  portalContainer,
}: ImageGalleryProps) {
  const dispatch = useAppDispatch()
  const activeChat = useAppSelector(selectActiveChat)
  const archive = useContext(ArchivedChatContext)
  const chatJid = archive?.chatJid ?? activeChat.jid
  const liveDeliveryOrders = useAppSelector(
    selectDeliveryOrdersFromChat(chatJid),
  )
  const deliveryOrders =
    archive?.chatMessages.messageIdsWithDeliveryOrder ?? liveDeliveryOrders
  const isChatWithSlickAI = ChatHelpers.isChatWithSlickAI(chatJid)
  const { visible, initialImage, messageId, directImage, externalSource } =
    useAppSelector(selectImageGallery)
  const onClose = useCallback(() => {
    dispatch(hideImageGallery())
    if (isMobile && !externalSource) {
      setMobileRoute(mobileRoutes.CHAT)
    }
  }, [dispatch, externalSource])
  const imgRef = useRef<HTMLElement | null>(null)
  const { myJid } = useContext(XmppContext)
  // Freeze messages at mount time so newly-loaded messages don't shift
  // images while the gallery is open.
  const [frozenMessages] = useState(() => messages)

  const messageToSearchForImages = useMemo(() => {
    if (messageId === "order")
      return [
        {
          from: getSlickshiftAIJid(myJid),
          to: chatJid,
          type: "groupchat",
          displayType: MessageDisplayType.Message,
          id: "order",
          body: "",
          markers: [],
          reactions: [],
          timestamp: new Date().toString(),
        } as Message,
      ]
    if (!messageId) return frozenMessages

    const targetMessage = frozenMessages.find(
      (message) => message.id === messageId,
    )
    return targetMessage ? [targetMessage] : []
  }, [messageId, myJid, chatJid, frozenMessages])

  const images = useMemo(() => {
    if (directImages && directImages.length > 0) {
      return directImages.map((img, i) => ({
        src: mapFileUrlToSameOrigin(img.src),
        key: `direct_${i}`,
        message: img.message,
      }))
    }
    if (directImage) {
      return [
        {
          src: mapFileUrlToSameOrigin(directImage.src),
          key: "direct",
          message: directImage.message,
        },
      ]
    }
    return messageToSearchForImages
      .filter((message) => {
        return (
          isMessageWithImage(message) ||
          (isChatWithSlickAI && isMarkdownWithImages(message)) ||
          (isChatWithSlickAI && isBlockWithImages(message)) ||
          isMessageWithOrder(message, deliveryOrders)
        )
      })
      .reduce<
        Array<{
          src: string
          key: string
          originRef?: React.RefObject<HTMLElement | null>
          message: Message
        }>
      >((acc, messageOrForwardedMessage) => {
        const message =
          messageOrForwardedMessage.forwarded || messageOrForwardedMessage

        const markdownLinks = extractMarkdownUrls(message.body)
        const blockLinks = extractUrlsFromStructuredMessage(message.body)
        const isInitialImage =
          message.url === initialImage ||
          (initialImage && markdownLinks.indexOf(initialImage) !== -1) ||
          (initialImage && blockLinks.indexOf(initialImage) !== -1) ||
          message.id === "order"

        if (isInitialImage) {
          imgRef.current = window.document.querySelector(
            `img[src="${message.url}"]`,
          )
        }

        if (markdownLinks.length > 0) {
          return [
            ...acc,
            ...markdownLinks.map((url, index) => ({
              src: mapFileUrlToSameOrigin(url),
              key: `${message.id}_${url}_${index}`,
              originRef: isInitialImage ? imgRef : undefined,
              message: { ...message, url },
            })),
          ]
        } else if (blockLinks.length > 0) {
          return [
            ...acc,
            ...blockLinks.map((url, index) => ({
              src: mapFileUrlToSameOrigin(url),
              key: `${message.id}_${url}_${index}`,
              originRef: isInitialImage ? imgRef : undefined,
              message: { ...message, url },
            })),
          ]
        } else if (isMessageWithOrder(message, deliveryOrders)) {
          const url = deliveryOrders?.[message.id]?.fileUrl
          if (url) {
            acc.push({
              src: mapFileUrlToSameOrigin(url!),
              key: message.id,
              originRef: isInitialImage ? imgRef : undefined,
              message,
            })
          } else if (
            deliveryOrders?.[message.id]?.files &&
            deliveryOrders[message.id].files!.length > 0
          ) {
            deliveryOrders[message.id].files!.forEach((file, index) => {
              if (!/\.pdf$/i.test(file.fileUrl)) {
                acc.push({
                  src: mapFileUrlToSameOrigin(file.fileUrl),
                  key: `${message.id}_${file.fileUrl}_${index}`,
                  originRef: isInitialImage ? imgRef : undefined,
                  message,
                })
              }
            })
          } else if (
            message.finishedRouteAlert?.fileUrl &&
            !/\.pdf$/i.test(message.finishedRouteAlert?.fileUrl)
          ) {
            acc.push({
              src: mapFileUrlToSameOrigin(message.finishedRouteAlert?.fileUrl),
              key: `${message.id}_${message.finishedRouteAlert?.fileUrl}`,
              originRef: isInitialImage ? imgRef : undefined,
              message,
            })
          }
        } else {
          const url =
            /\.pdf/i.test(message.url!) && message.thumbnail?.uri
              ? message.thumbnail.uri
              : message.url

          acc.push({
            src: mapFileUrlToSameOrigin(url!),
            key: message.id,
            originRef: isInitialImage ? imgRef : undefined,
            message,
          })
        }

        return acc
      }, [])
  }, [
    directImages,
    directImage,
    messageToSearchForImages,
    isChatWithSlickAI,
    deliveryOrders,
    initialImage,
  ])

  const initialIndex = useMemo(() => {
    // When using directImages, find the clicked image via directImage.src
    if (directImages && directImage) {
      const mappedSrc = mapFileUrlToSameOrigin(directImage.src)
      const idx = images.findIndex((i) => i.src === mappedSrc)
      return idx >= 0 ? idx : 0
    }
    return initialImage ? images.findIndex((i) => i.src === initialImage) : 0
  }, [initialImage, images, directImages, directImage])

  const [index, setIndex] = useState(initialIndex)

  // If the message with the last image has been deleted,
  // we need to adjust the index to the latest available image.
  const adjustedIndex = index >= images.length ? images.length - 1 : index

  const selectedImage = images[adjustedIndex]

  const hookResult = useCMRMetadata(selectedImage?.message)

  // Use per-image overrides (for multi-image mode), then single overrides, then hook
  const perImageOverride = metadataOverrides?.[selectedImage?.src]
  const effectiveMetadata =
    perImageOverride?.extractedMetadata ||
    metadataOverride ||
    hookResult.documentMetadata?.data
  const effectiveDriverContext =
    perImageOverride?.driverContext ||
    driverContextOverride ||
    hookResult.driverContext
  const effectiveDocumentType =
    perImageOverride?.documentType ||
    documentTypeOverride ||
    hookResult.documentMetadata?.documentType
  const effectiveBlurry =
    perImageOverride?.blurry ?? hookResult.documentMetadata?.blurry
  const effectiveRemarks =
    perImageOverride?.remarks ?? hookResult.documentMetadata?.remarks
  const hasCMRMetadata = Boolean(effectiveMetadata || effectiveDriverContext)

  // If there is no image, then close the gallery. This could happen if
  // someone has deleted a message when someone else has the gallery open.

  if (images.length === 0) {
    onClose()
    return null
  }

  return (
    <>
      {hasCMRMetadata && !isMobile && (
        <CMRMetaData
          showAllData
          className="fixed left-0 top-0 z-[10001] h-screen w-[30%] overflow-y-auto bg-[var(--panel-color)] p-4"
          message={selectedImage?.message}
          metadata={effectiveMetadata}
          driverContext={effectiveDriverContext}
          documentType={effectiveDocumentType}
          blurry={effectiveBlurry}
          remarks={effectiveRemarks}
          linkInfo={linkInfoMap?.[selectedImage?.src]}
          onLinked={onLinked}
        />
      )}
      <PhotoSlider
        portalContainer={portalContainer}
        /*
         * Overwrite z-index of PhotoView-Portal
         * The default z-index of 2000 causes issues
         * with toast visibility, as toast overlays have a lower z-index.
         * Reducing this z-index ensures toasts appear on top of the open gallery,
         * maintaining proper visibility of important notifications.
         */
        className={`${hasCMRMetadata && !isMobile ? "cmr" : ""} !z-[10000]`}
        images={images}
        visible={visible}
        onClose={onClose}
        index={adjustedIndex}
        onIndexChange={setIndex}
        loadingElement={<Loader />}
        toolbarRender={(props) => <ImageTopToolbar {...props} />}
        overlayRender={(props) =>
          images[props.index] &&
          images[props.index].message && <ImageBottomToolbar {...props} />
        }
      />
    </>
  )
}
